import {
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  TEAMS_SEARCH_PEOPLE,
  TEAMS_SEARCH_CHANNEL,
} from '../helpers/constants.js';

/**
 * Authenication handler service for TEAMS authentication and API calls
 */
export class MicrosoftAuthenticationHandler {
  /**
   * @property {Object} msalConfig - configuration for microsoft API
   * @property {Object} tokenRequestProp - options for token retrieval
   * for user authentication
   * @property {string} username
   * @property {PublicClientApplication} msalObj - an instance of the
   * PublicClientApplication from the @azue/msal-browser package
   * @property {string} graphApiBase - A URI for Microsoft's graph API
   */
  constructor() {
    this.msalConfig = {
      auth: {
        clientId: '6126beb6-41df-4f8d-9c8f-66b8747ac19c',
        authority:
          'https://login.microsoftonline.com/41eb501a-f671-4ce0-a5bf-b64168c3705f',
        redirectUri: `${window.location.origin}/blank`,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) {
              return;
            }
            switch (level) {
              case LogLevel.Error:
                // eslint-disable-next-line no-console
                console.error(message);
                break;
              case LogLevel.Info:
                break;
              case LogLevel.Verbose:
                break;
              case LogLevel.Warning:
                // eslint-disable-next-line no-console
                console.warn(message);
                break;
              default:
              // do nothing
            }
          },
        },
      },
    };
    this.tokenRequestProp = {
      scopes: [
        'User.Read',
        'User.ReadBasic.All',
        'Team.ReadBasic.All',
        'Channel.ReadBasic.All',
        'ChannelMessage.send',
        'Chat.ReadWrite',
      ],
      forceRefresh: false, // Set this to "true" to skip a cached token and go to the server to get a new token
    };
    this.username = '';
    this.msalObj = new PublicClientApplication(this.msalConfig);
    this.graphApiBase = 'https://graph.microsoft.com/v1.0/';
    this.initialized = false;
  }

  /**
   * msal-browser >= v3 requires that the application be initialized
   * The initialize method is asynchronous.
   */
  async init() {
    await this.msalObj.initialize();
    this.initialized = true;
  }

  /**
   * Helper function to call MS Graph API endpoint (GET) using the authorization bearer token scheme
   * @param {string} endpoint - The endpoint to fetch from
   * @param {string} token - MS auth token
   * @param {Object} customHeaders - Custom headers to merge with defaults
   * @returns {Promise}
   */
  static async msGraphGet(endpoint, token, customHeaders) {
    const options = {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    };
    options.headers = { ...options.headers, ...customHeaders };

    return fetch(endpoint, options)
      .then(response => response.json())
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  }

  /**
   * Helper function to call MS Graph API endpoint via POST using the authorization bearer token scheme
   * @param {string} endpoint - The endpoint to fetch from
   * @param {string} token - MS auth token
   * @param {Object} customHeaders - Custom headers to merge with defaults
   * @returns {Promise}
   */
  static async msGraphPost(endpoint, token, content) {
    const bearer = `Bearer ${token}`;

    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: bearer,
      },
      body: JSON.stringify(content),
    };

    return fetch(endpoint, options)
      .then(response => response.json())
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  }

  /**
   * See here for more info on account retrieval:
   * - https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   * @throws {Error} - if call fails due to anything other than the
   * interaction requirement
   * @returns {Promise}
   */
  async getToken() {
    /* eslint-disable consistent-return, no-else-return */
    if (!this.username) {
      const currentAccounts = this.msalObj.getAllAccounts();
      if (!currentAccounts.length) {
        const response = await this.msalObj.loginPopup(this.tokenRequestProp);
        this.username = response.account.username;
      } else {
        this.username = currentAccounts[0].username;
      }
    }

    const request = Object.assign(this.tokenRequestProp);
    request.account = this.msalObj.getAccountByUsername(this.username);
    this.selfName = request.account.name;
    this.selfId = request.account.localAccountId;
    return this.msalObj.acquireTokenSilent(request).catch(async error => {
      // eslint-disable-next-line no-console
      console.warn(
        'Silent token acquisition failed. Acquiring token using popup.'
      );
      if (error instanceof InteractionRequiredAuthError) {
        // fallback to interaction when silent call fails, e.g. if the token has expired.
        return this.msalObj
          .acquireTokenPopup(request)
          .then(tokenResponse => tokenResponse);
      }

      throw error;
    });
    /* eslint-enable consistent-return, no-else-return */
  }

  /**
   * Get the teams which a user is a member of under their account.
   * @param {Function} filterFn - A callback that can be passed to Array.filter
   * for filtering the teams response
   * @returns {Object[]} - An array of mapped and/or filtered teams
   */
  async getTeams(filterFn) {
    const tokenResponse = await this.getToken();
    const teamsResponse = await MicrosoftAuthenticationHandler.msGraphGet(
      `${this.graphApiBase}me/joinedTeams`,
      tokenResponse.accessToken
    );

    let clientTeams = teamsResponse.value;
    if (filterFn) clientTeams = clientTeams.filter(filterFn);
    return clientTeams.map(x => ({ displayName: x.displayName, id: x.id }));
  }

  /**
   * Get the members in a particular team.
   * @param teamId {String} String team id.
   * @returns {Object[]} - A list of Objects containing channel ids and channel display names.
   */
  async getTeamsMembers(teamId) {
    const tokenResponse = await this.getToken();
    const teamsResponse = await MicrosoftAuthenticationHandler.msGraphGet(
      `${this.graphApiBase}teams/${teamId}/members`,
      tokenResponse.accessToken
    );
    if (teamsResponse.error) {
      return Promise.reject(new Error(teamsResponse.error.message));
    }
    // Remove those without a displayName & only keep displayName & userId
    const filtered = [];
    teamsResponse.value.forEach(({ displayName, userId }) => {
      if (displayName) {
        filtered.push({ displayName, userId });
      }
    });

    return filtered;
  }

  /**
   * Get the channels which exist under a particular team.
   * @param {String} teamId
   * @param {String} teamName
   * @param {String} searchFilter - A search filter for the results
   * @returns {Object[]} - a list of Objects containing channel ids and channel display names.
   */
  async getChannels(teamId, teamName, searchFilter) {
    let teamsResponse;
    /**
     * We want to return all the Channels under a specific Team if the user is searching by Team name
     * First, check if teamName includes searchFilter, if it does, return all the channels for that team
     * This replicates the behavior of $filter & Contains function
     */
    const isTeamName = teamName
      .toLowerCase()
      .includes(searchFilter.toLowerCase());
    const tokenResponse = await this.getToken();
    if (isTeamName) {
      teamsResponse = await MicrosoftAuthenticationHandler.msGraphGet(
        `${this.graphApiBase}teams/${teamId}/channels`,
        tokenResponse.accessToken
      );
    } else {
      const encodedfilter = this.prepFilterParam(searchFilter);
      teamsResponse = await MicrosoftAuthenticationHandler.msGraphGet(
        `${this.graphApiBase}teams/${teamId}/channels?$filter=contains(tolower(displayName), tolower('${encodedfilter}'))`,
        tokenResponse.accessToken
      );
    }
    if (teamsResponse.error) {
      return Promise.reject(new Error(teamsResponse.error.message));
    }
    return teamsResponse.value.map(x => ({
      displayName: x.displayName,
      id: x.id,
      teamName,
      teamId,
    }));
  }

  /**
   * Get the people that have names containing the search String
   * @param {String} searchFilter - String search filter.
   * @returns {Object[]} - a list of Objects containing people ids and people display names.
   */
  async getPeople(searchFilter) {
    const tokenResponse = await this.getToken();
    // Some advanced queries require this header
    const headers = { ConsistencyLevel: 'eventual' };

    // $search requires escaped backslash and double quote
    let encodedfilter = searchFilter.replaceAll(/\\|"/g, char => `\\${char}`);
    // $search seems to decode value twice so we need to encode twice to pass the correct filter
    encodedfilter = encodeURIComponent(encodeURIComponent(encodedfilter));

    // Exclude any results that have id matching the user because they cannot send themself Teams msgs)
    const { error: teamsError, value: people } =
      await MicrosoftAuthenticationHandler.msGraphGet(
        `${this.graphApiBase}users?$search="displayName:${encodedfilter}"&$filter=NOT(id eq '${this.selfId}')`,
        tokenResponse.accessToken,
        headers
      );

    if (teamsError) {
      return Promise.reject(new Error(teamsError.message));
    }

    // Use Regular Expressions to get agency name (inside the parentheses in displayName)
    const agencyNameRegex = /\(([^)]+)\)/;

    // Sort people alphabetically by agency name first, and then by display name
    // Those without an agency name come last
    return (
      people
        .sort((a, b) => {
          const agencyA = agencyNameRegex.exec(a.displayName)?.[1];
          const agencyB = agencyNameRegex.exec(b.displayName)?.[1];

          if (agencyA?.toLowerCase() === agencyB?.toLowerCase()) {
            return a.displayName
              .toLowerCase()
              .localeCompare(b.displayName.toLowerCase());
          }
          return agencyA?.toLowerCase().localeCompare(agencyB?.toLowerCase());
        })
        // Only keep displayName, id, and create key "members" to follow same structure as other recipient types
        .map(({ displayName, id }) => ({
          displayName,
          id,
          members: [{ displayName, userId: id }],
        }))
    );
  }

  /**
   * Replaces individual single quotes with 2 single quotes for $filter ' -> ''
   * @param {string} str - The $filter string to modify and encode
   * @returns {string} - The encoded version of the `str` argument
   */
  // eslint-disable-next-line class-methods-use-this
  prepFilterParam(str) {
    const encodedStr = str.replaceAll("'", () => "''");
    return encodeURIComponent(encodedStr);
  }

  /**
   * Call the Microsoft Teams API endpoint with the interpolated card
   * template to be sent
   * @param {Object} cardContent
   * @param {Object} data - Teams data for api call
   * @param {string} data.type - The type of destination for the content
   * @param {string} data.id - The ID of the chat destination
   * @param {string} data.teamId - The ID of the team chat destination
   * @returns {Promise} - MS Graph call fetch
   */
  /* eslint-disable consistent-return, default-case, no-case-declarations */
  async sendCard(cardContent, data) {
    // eslint-disable-next-line default-case
    switch (data.type) {
      case TEAMS_SEARCH_PEOPLE:
        // When user selects an individual, there is no chatId so we create a chatId with the user and selected individual
        const memberIds = [this.selfId, data.id];
        const resp = await this.createChatWithIndividual(memberIds);
        if (resp.error) return resp;
        return this.sendCardToChat(cardContent, resp.id);
      case TEAMS_SEARCH_CHANNEL:
        return this.sendCardToChannel(cardContent, data.teamId, data.id);
    }
  }
  /* eslint-enable consistent-return, default-case, no-case-declarations */

  /**
   * @param {string[]} ids - A list of members to be included in the chat
   * @returns {Promise} - MS Graph call fetch
   */
  async createChatWithIndividual(ids) {
    const tokenResponse = await this.getToken();
    const chatDetails = {
      chatType: 'oneOnOne',
      members: ids.map(id => ({
        '@odata.type': '#microsoft.graph.aadUserConversationMember',
        roles: ['owner'],
        'user@odata.bind': `https://graph.microsoft.com/v1.0/users('${id}')`,
      })),
    };

    return MicrosoftAuthenticationHandler.msGraphPost(
      `${this.graphApiBase}chats`,
      tokenResponse.accessToken,
      chatDetails
    );
  }

  /**
   * @param {string} cardContent - The interpolated card template
   * @param {string} id - Chat ID to send the card to
   * @returns {Promise} - MS Graph call fetch
   */
  async sendCardToChat(cardContent, id) {
    const tokenResponse = await this.getToken();
    return MicrosoftAuthenticationHandler.msGraphPost(
      `${this.graphApiBase}chats/${id}/messages`,
      tokenResponse.accessToken,
      cardContent
    );
  }

  /**
   * @param {string} cardContent - The interpolated card template
   * @param {string} teamId - Team ID for the channel
   * @param {string} channelId - Channel ID to send the card to
   * @returns {Promise} - MS Graph call fetch
   */
  async sendCardToChannel(cardContent, teamId, channelId) {
    const tokenResponse = await this.getToken();
    return MicrosoftAuthenticationHandler.msGraphPost(
      `${this.graphApiBase}teams/${teamId}/channels/${channelId}/messages`,
      tokenResponse.accessToken,
      cardContent
    );
  }

  /**
   * Get the details of team members by their ids. A maximum of 20 ids can be passed in at time. Limit set by MS Graph.
   * @param {String[]} teamMemberIds - Array of team member ids.
   * @returns {Object[]} - a list of Objects containing team member details.
   */
  async getTeamMembersDetails(teamMemberIds) {
    const tokenResponse = await this.getToken();
    const batchRequestBody = {
      requests: teamMemberIds.map((id, index) => ({
        id: index,
        method: 'GET',
        url: `users/${id}`,
      })),
    };
    const value = await MicrosoftAuthenticationHandler.msGraphPost(
      `${this.graphApiBase}$batch`,
      tokenResponse.accessToken,
      batchRequestBody
    );

    return value.responses.map(response => response.body);
  }
}
